import { toString } from '@pequity/shared/utils';
import { type AxiosResponseHeaders } from 'axios';
import { useI18n } from 'vue-i18n';
import { type FieldType, type Wave } from '@/ccp-mfe/types';
import type { CurrencyDisplay } from '@/ccp-mfe/types/constants';
import { formatToCurrency } from '@/ccp-mfe/utils/currency';

export const isRecommendedField = (name: string) => {
  return name.endsWith('_recommended') || name.endsWith('_recommended_override');
};

export const getRecommendedValue = (employeeData: Record<string, unknown>, fieldName: string) => {
  const recommendedOverride = employeeData[`${fieldName}_recommended_override`] as string | null;
  const recommended = employeeData[`${fieldName}_recommended`] as string | null;
  return recommendedOverride ?? recommended;
};

export const getListLink = () => ({
  name: 'ccp-mfe-list',
});

export const getDashboardLink = (cycleId: number) => ({
  name: 'ccp-mfe-dashboard',
  params: { cycleId },
});

export const getDetailsAdminLink = (cycleId: number) => ({
  name: 'ccp-mfe-admin-details',
  params: { cycleId },
});

export const getDetailsGroupLink = (cycleId: number, groupId: number, roleId: number) => ({
  name: 'ccp-mfe-group-details',
  params: { cycleId, groupId, roleId },
});

export const getSettingsAdminLink = (cycleId: number) => ({
  name: 'ccp-mfe-settings',
  params: { cycleId },
});

export const getSettingsGroupLink = (cycleId: number, groupId: number, roleId: number) => ({
  name: 'ccp-mfe-group-settings',
  params: { cycleId, groupId, roleId },
});

export const getSettingsPlanningHierarchyWaveLink = (cycleId: number, waveId: number) => ({
  name: 'ccp-mfe-settings-planning-hierarchy-wave',
  params: { cycleId, waveId },
});

export const getColumnsConfigLink = (cycleId: number) => ({
  name: 'ccp-mfe-columns-config',
  params: { cycleId },
});

export const getColumnsConfigComputationLink = (cycleId: number, columnName: string) => ({
  name: 'ccp-mfe-columns-config-computation',
  params: { cycleId, columnName },
});

export const getColumnsConfigComputationErrorLink = (cycleId: number, columnName: string) => ({
  name: 'ccp-mfe-columns-config-computation',
  params: { cycleId, columnName, status: 'error' },
});

export const createFieldPlaceholder = (
  type: FieldType | string,
  value: number,
  currency = 'USD',
  precision = 0,
  currencyDisplay: CurrencyDisplay = 'code'
) => {
  if (type === 'inputCurrency' || type === 'inputCurrencyInfo') {
    return formatToCurrency(value, currency, precision, currencyDisplay) || '';
  }

  return toString(value);
};

export const useCycleStatusLabel = () => {
  const { t } = useI18n();

  const getCycleStatusLabel = (waves: Wave[]) => {
    if (waves.length === 1) {
      return t('ccp.card_status_wave_in_progress', { name: waves[0].name });
    }
    if (waves.length > 1) {
      const comma = waves.length > 2 ? ',' : '';
      const wavesPhrase = waves.reduce((acc, curr, i) => {
        return acc + (!(i === waves.length - 1) ? `${curr.name}${comma} ` : `and ${curr.name}`);
      }, '');
      return t('ccp.card_status_waves_in_progress', { names: wavesPhrase });
    }
    return t('ccp.card_status_no_waves');
  };

  return {
    getCycleStatusLabel,
  };
};

const getFilenameFromHeaders = (headers: Partial<AxiosResponseHeaders>): string => {
  let matches = /filename="([^"]*)"/.exec(headers?.['content-disposition'] || '');
  if (!matches || !matches[1]) {
    matches = /filename\*=utf-8''([^']+)/.exec(headers?.['content-disposition'] || '');
    if (matches && matches[1]) {
      return decodeURIComponent(matches[1]);
    }
  }

  return matches?.[1] || 'download';
};

export const downloadBlob = (data: Blob, headers: Partial<AxiosResponseHeaders>) => {
  const link = document.createElement('a');
  link.href = URL.createObjectURL(data);
  const fileName = getFilenameFromHeaders(headers);
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
};

export const createAndDownloadCsv = (filename: string, items: Record<string, unknown>[]) => {
  let csv = '';

  for (let row = 0; row < items.length; row++) {
    const keysAmount = Object.keys(items[row]).length;
    let keysCounter = 0;

    // Generate the headings
    if (row === 0) {
      for (const key in items[row]) {
        // Do not add a comma at the last cell
        // The '\r\n' adds a line break
        csv += key + (keysCounter + 1 < keysAmount ? ',' : '\r\n');
        keysCounter++;
      }
    } else {
      for (const key in items[row]) {
        csv += items[row][key] + (keysCounter + 1 < keysAmount ? ',' : '\r\n');
        keysCounter++;
      }
    }

    keysCounter = 0;
  }

  // Download the .csv by creating a link
  const link = document.createElement('a');
  link.setAttribute('href', 'data:text/plain;charset=utf-8,' + encodeURIComponent(csv));
  link.setAttribute('download', `${filename}.csv`);
  document.body.appendChild(link);
  link.click();
  link.remove();
};
